import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { CheckCircle } from 'lucide-react';
import feedbackService from '@/services/feedbackService.ts';
import type { FeedbackCategory } from '@/types/feedback.ts';
import CustomSelect from '@/components/ui/CustomSelect.tsx';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  createFeedbackSchema,
  type FeedbackFormValues,
} from '@/schemas/feedbackSchema.ts';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function FeedbackModal({ isOpen, onClose }: Props) {
  const { t } = useTranslation();
  const schema = useMemo(() => createFeedbackSchema(t), [t]);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const categoryOptions: { value: FeedbackCategory; label: string }[] = [
    { value: 'Bug', label: t('feedback.categoryBug') },
    { value: 'Suggestion', label: t('feedback.categorySuggestion') },
    { value: 'GeneralExperience', label: t('feedback.categoryGeneral') },
  ];

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FeedbackFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { category: 'GeneralExperience' },
  });

  const onSubmit = async (data: FeedbackFormValues) => {
    setError('');
    setIsSubmitting(true);
    try {
      await feedbackService.create({
        category: data.category,
        liked: data.liked || undefined,
        improvements: data.improvements || undefined,
        comments: data.comments || undefined,
      });
      reset();
      setShowSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('feedback.submitError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    reset();
    setError('');
    setShowSuccess(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="sm:max-w-md">
        {showSuccess ? (
          <div className="flex flex-col items-center py-8">
            <div className="flex size-14 items-center justify-center rounded-full bg-emerald-500/10">
              <CheckCircle className="size-8 text-emerald-500" />
            </div>
            <h3 className="mt-4 text-lg font-semibold text-foreground">
              {t('feedback.successTitle')}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('feedback.successMessage')}
            </p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{t('feedback.modalTitle')}</DialogTitle>
              <DialogDescription>{t('feedback.modalSubtitle')}</DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t('feedback.categoryLabel')}
                </label>
                <CustomSelect
                  value={watch('category')}
                  onChange={(val) => setValue('category', val as FeedbackCategory)}
                  options={categoryOptions.map((opt) => ({ value: opt.value, label: opt.label }))}
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">{t('feedback.likedLabel')}</label>
                <textarea
                  {...register('liked')}
                  rows={2}
                  className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                  placeholder={t('feedback.likedPlaceholder')}
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">{t('feedback.improvementsLabel')}</label>
                <textarea
                  {...register('improvements')}
                  rows={2}
                  className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                  placeholder={t('feedback.improvementsPlaceholder')}
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">{t('feedback.commentsLabel')}</label>
                <textarea
                  {...register('comments')}
                  rows={2}
                  className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                  placeholder={t('feedback.commentsPlaceholder')}
                />
                {errors.comments && (
                  <p className="mt-1 text-xs text-destructive">{errors.comments.message}</p>
                )}
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <DialogFooter>
                <Button type="button" variant="ghost" onClick={handleClose}>{t('common.cancel')}</Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? t('feedback.submitting') : t('feedback.submit')}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
